import {

  createAsyncThunk,

  createSlice,

} from "@reduxjs/toolkit";



import API from "../../utils/apiCalling";



const api = new API();



/*

|--------------------------------------------------------------------------

| DEFAULT STATE

|--------------------------------------------------------------------------

*/



const defaultFilters = {

  search: "",

  documentStatus: "",

  paymentStatus: "",

  fulfillmentStatus: "",

  conversionStatus: "",

  salesChannel: "",

  sourceType: "",



  customerId: "",

  salespersonId: "",

  branchId: "",

  firmId: "",

  warehouseId: "",



  fromDate: "",

  toDate: "",

  validUntilFrom: "",

  validUntilTo: "",



  sortBy: "documentDate",

  sortOrder: "desc",



  page: 1,

  limit: 20,

};



const defaultPagination = {

  page: 1,

  limit: 20,

  total: 0,

  totalPages: 0,

  hasNextPage: false,

  hasPreviousPage: false,

};



const defaultSummary = {

  totalCount: 0,

  totalAmount: 0,



  draftCount: 0,

  confirmedCount: 0,

  cancelledCount: 0,



  convertedCount: 0,

  pendingConversionCount: 0,

  expiredCount: 0,



  paidAmount: 0,

  balanceAmount: 0,

};



/*

|--------------------------------------------------------------------------

| RESPONSE HELPERS

|--------------------------------------------------------------------------

*/



const getResponseData = (

  response,

  fallback = {}

) => {

  if (!response) {

    return fallback;

  }



  return (

    response.data ||

    response.result ||

    response

  );

};



const getErrorMessage = (

  response,

  fallbackMessage

) => {

  return (

    response?.message ||

    response?.error?.message ||

    response?.error ||

    fallbackMessage

  );

};



const isFailedResponse = (

  response

) => {

  return (

    !response ||

    response.success === false ||

    response.status === "error"

  );

};



const rejectFailedResponse = (

  response,

  rejectWithValue,

  fallbackMessage

) => {

  if (

    !isFailedResponse(

      response

    )

  ) {

    return null;

  }



  return rejectWithValue(

    getErrorMessage(

      response,

      fallbackMessage

    )

  );

};



/*

|--------------------------------------------------------------------------

| VALUE HELPERS

|--------------------------------------------------------------------------

*/



const capitalizeFirst = (

  value = ""

) => {

  if (!value) {

    return "";

  }



  return (

    value.charAt(0).toUpperCase() +

    value.slice(1)

  );

};



const getDocumentFromPayload = (

  payload,

  documentKeys = []

) => {

  if (!payload) {

    return null;

  }



  for (

    const key of

    documentKeys

  ) {

    if (payload?.[key]) {

      return payload[key];

    }

  }



  return (

    payload.document ||

    payload.invoice ||

    payload

  );

};



const getListFromPayload = (

  payload,

  listKeys = []

) => {

  if (!payload) {

    return [];

  }



  for (

    const key of

    listKeys

  ) {

    if (

      Array.isArray(

        payload?.[key]

      )

    ) {

      return payload[key];

    }

  }



  return (

    payload.documents ||

    payload.records ||

    payload.items ||

    []

  );

};



const updateDocumentInList = (

  state,

  document

) => {

  if (!document?._id) {

    return;

  }



  const index =

    state.documents.findIndex(

      (item) =>

        item._id ===

        document._id

    );



  if (index >= 0) {

    state.documents[index] =

      document;

  }

};



const addDocumentToList = (

  state,

  document

) => {

  if (!document?._id) {

    return;

  }



  const exists =

    state.documents.some(

      (item) =>

        item._id ===

        document._id

    );



  if (!exists) {

    state.documents.unshift(

      document

    );

  }

};



/*

|--------------------------------------------------------------------------

| CREATE BILLING DOCUMENT MODULE

|--------------------------------------------------------------------------

|

| Returns:

|

| {

|   reducer,

|   actions,

|   thunks,

|   selectors,

|   initialState

| }

|--------------------------------------------------------------------------

*/



const createDocumentSlice = ({

  sliceName,

  stateKey = sliceName,



  documentName,

  documentType,



  documentKey,

  documentKeys = [],



  listKey,

  listKeys = [],



  urls,



  initialFilters = {},

  initialSummary = {},

  initialDropdowns = {},



  supportsConfirm = true,

  supportsCancel = true,

  supportsDuplicate = true,

  supportsConvert = true,

  supportsDelete = true,

  supportsHistory = true,

} = {}) => {

  if (!sliceName) {

    throw new Error(

      "createDocumentSlice: sliceName is required."

    );

  }



  if (!documentName) {

    throw new Error(

      "createDocumentSlice: documentName is required."

    );

  }



  if (!urls?.list) {

    throw new Error(

      `createDocumentSlice: list URL is required for ${sliceName}.`

    );

  }



  const actionPrefix =

    `billing/${sliceName}`;



  const resolvedDocumentKeys = [

    documentKey,

    ...documentKeys,

    "document",

  ].filter(Boolean);



  const resolvedListKeys = [

    listKey,

    ...listKeys,

    "documents",

  ].filter(Boolean);



  const resolvedInitialFilters = {

    ...defaultFilters,

    ...initialFilters,

  };



  const resolvedInitialSummary = {

    ...defaultSummary,

    ...initialSummary,

  };



  const createInitialState =

    () => ({

      documents: [],

      selectedDocument: null,



      summary: {

        ...resolvedInitialSummary,

      },



      createData: null,



      dropdowns: {

        documentType,

        ...initialDropdowns,

      },



      history: [],



      historyPagination: {

        ...defaultPagination,

      },



      filters: {

        ...resolvedInitialFilters,

      },



      pagination: {

        ...defaultPagination,

      },



      listLoading: false,

      detailsLoading: false,

      summaryLoading: false,

      createDataLoading: false,

      dropdownsLoading: false,

      historyLoading: false,



      createLoading: false,

      updateLoading: false,

      confirmLoading: false,

      cancelLoading: false,

      duplicateLoading: false,

      convertLoading: false,

      deleteLoading: false,



      error: null,

      actionError: null,



      operationSuccess: false,

      operationMessage: "",



      lastCreatedDocument: null,

      lastDuplicatedDocument: null,

      lastConvertedDocument: null,

    });



  /*

  |--------------------------------------------------------------------------

  | FETCH LIST

  |--------------------------------------------------------------------------

  */



  const fetchDocuments =

    createAsyncThunk(

      `${actionPrefix}/fetchDocuments`,

      async (

        params = {},

        {

          getState,

          rejectWithValue,

        }

      ) => {

        const state =

          getState();



        const storedFilters =

          state?.[stateKey]

            ?.filters ||

          resolvedInitialFilters;



        const response =

          await api.getWithParams(
            urls.list,

            {

              ...storedFilters,

              ...params,

            }

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to fetch ${documentName.toLowerCase()} list.`

          );



        if (failed) {

          return failed;

        }



        return getResponseData(

          response,

          {}

        );

      }

    );



  /*

  |--------------------------------------------------------------------------

  | FETCH SUMMARY

  |--------------------------------------------------------------------------

  */



  const fetchSummary =

    createAsyncThunk(

      `${actionPrefix}/fetchSummary`,

      async (

        params = {},

        {

          getState,

          rejectWithValue,

        }

      ) => {

        if (!urls.summary) {

          return rejectWithValue(

            `${documentName} summary API is not configured.`

          );

        }



        const state =

          getState();



        const storedFilters =

          state?.[stateKey]

            ?.filters ||

          resolvedInitialFilters;



        const response =

          await api.getWithParams(
            urls.summary,

            {

              ...storedFilters,

              ...params,



              page:

                undefined,



              limit:

                undefined,



              sortBy:

                undefined,



              sortOrder:

                undefined,

            }

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to fetch ${documentName.toLowerCase()} summary.`

          );



        if (failed) {

          return failed;

        }



        return getResponseData(

          response,

          {}

        );

      }

    );



  /*

  |--------------------------------------------------------------------------

  | FETCH CREATE DATA

  |--------------------------------------------------------------------------

  */



  const fetchCreateData =

    createAsyncThunk(

      `${actionPrefix}/fetchCreateData`,

      async (

        params = {},

        {

          rejectWithValue,

        }

      ) => {

        if (!urls.createData) {

          return rejectWithValue(

            `${documentName} create-data API is not configured.`

          );

        }



        const response =

          await api.get(

            urls.createData,

            params

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to fetch ${documentName.toLowerCase()} create data.`

          );



        if (failed) {

          return failed;

        }



        return getResponseData(

          response,

          {}

        );

      }

    );



  /*

  |--------------------------------------------------------------------------

  | FETCH DROPDOWNS

  |--------------------------------------------------------------------------

  */



  const fetchDropdowns =

    createAsyncThunk(

      `${actionPrefix}/fetchDropdowns`,

      async (

        _,

        {

          rejectWithValue,

        }

      ) => {

        if (!urls.dropdowns) {

          return rejectWithValue(

            `${documentName} dropdown API is not configured.`

          );

        }



        const response =

          await api.get(

            urls.dropdowns

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to fetch ${documentName.toLowerCase()} dropdowns.`

          );



        if (failed) {

          return failed;

        }



        return getResponseData(

          response,

          {}

        );

      }

    );



  /*

  |--------------------------------------------------------------------------

  | FETCH BY ID

  |--------------------------------------------------------------------------

  */



  const fetchDocumentById =

    createAsyncThunk(

      `${actionPrefix}/fetchDocumentById`,

      async (

        documentId,

        {

          rejectWithValue,

        }

      ) => {

        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (

          typeof urls.getById !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} details API is not configured.`

          );

        }



        const response =

          await api.get(

            urls.getById(

              documentId

            )

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to fetch ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return getResponseData(

          response,

          {}

        );

      }

    );



  /*

  |--------------------------------------------------------------------------

  | CREATE

  |--------------------------------------------------------------------------

  */



  const createDocument =

    createAsyncThunk(

      `${actionPrefix}/createDocument`,

      async (

        payload,

        {

          rejectWithValue,

        }

      ) => {

        if (!urls.create) {

          return rejectWithValue(

            `${documentName} create API is not configured.`

          );

        }



        const response =

          await api.post(

            urls.create,

            payload || {}

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to create ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return {

          data:

            getResponseData(

              response,

              {}

            ),



          message:

            response?.message ||

            `${documentName} created successfully.`,

        };

      }

    );



  /*

  |--------------------------------------------------------------------------

  | UPDATE

  |--------------------------------------------------------------------------

  */



  const updateDocument =

    createAsyncThunk(

      `${actionPrefix}/updateDocument`,

      async (

        {

          documentId,

          payload,

        },

        {

          rejectWithValue,

        }

      ) => {

        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (

          typeof urls.update !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} update API is not configured.`

          );

        }



        const response =

          await api.put(

            urls.update(

              documentId

            ),

            payload || {}

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to update ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return {

          data:

            getResponseData(

              response,

              {}

            ),



          message:

            response?.message ||

            `${documentName} updated successfully.`,

        };

      }

    );



  /*

  |--------------------------------------------------------------------------

  | CONFIRM

  |--------------------------------------------------------------------------

  */



  const confirmDocument =

    createAsyncThunk(

      `${actionPrefix}/confirmDocument`,

      async (

        {

          documentId,

          payload = {},

        },

        {

          rejectWithValue,

        }

      ) => {

        if (!supportsConfirm) {

          return rejectWithValue(

            `${documentName} confirmation is not supported.`

          );

        }



        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (

          typeof urls.confirm !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} confirm API is not configured.`

          );

        }



        const response =

          await api.patch(

            urls.confirm(

              documentId

            ),

            payload

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to confirm ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return {

          data:

            getResponseData(

              response,

              {}

            ),



          message:

            response?.message ||

            `${documentName} confirmed successfully.`,

        };

      }

    );



  /*

  |--------------------------------------------------------------------------

  | CANCEL

  |--------------------------------------------------------------------------

  */



  const cancelDocument =

    createAsyncThunk(

      `${actionPrefix}/cancelDocument`,

      async (

        {

          documentId,

          reason,

          payload = {},

        },

        {

          rejectWithValue,

        }

      ) => {

        if (!supportsCancel) {

          return rejectWithValue(

            `${documentName} cancellation is not supported.`

          );

        }



        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (!reason?.trim()) {

          return rejectWithValue(

            "Cancellation reason is required."

          );

        }



        if (

          typeof urls.cancel !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} cancel API is not configured.`

          );

        }



        const response =

          await api.patch(

            urls.cancel(

              documentId

            ),

            {

              ...payload,



              reason:

                reason.trim(),

            }

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to cancel ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return {

          data:

            getResponseData(

              response,

              {}

            ),



          message:

            response?.message ||

            `${documentName} cancelled successfully.`,

        };

      }

    );



  /*

  |--------------------------------------------------------------------------

  | DUPLICATE

  |--------------------------------------------------------------------------

  */



  const duplicateDocument =

    createAsyncThunk(

      `${actionPrefix}/duplicateDocument`,

      async (

        {

          documentId,

          payload = {},

        },

        {

          rejectWithValue,

        }

      ) => {

        if (!supportsDuplicate) {

          return rejectWithValue(

            `${documentName} duplication is not supported.`

          );

        }



        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (

          typeof urls.duplicate !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} duplicate API is not configured.`

          );

        }



        const response =

          await api.post(

            urls.duplicate(

              documentId

            ),

            payload

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to duplicate ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return {

          data:

            getResponseData(

              response,

              {}

            ),



          message:

            response?.message ||

            `${documentName} duplicated successfully.`,

        };

      }

    );



  /*

  |--------------------------------------------------------------------------

  | CONVERT

  |--------------------------------------------------------------------------

  */



  const convertDocument =

    createAsyncThunk(

      `${actionPrefix}/convertDocument`,

      async (

        {

          documentId,

          targetType,

          saveAsDraft = true,

          payments = [],

          ...payload

        },

        {

          rejectWithValue,

        }

      ) => {

        if (!supportsConvert) {

          return rejectWithValue(

            `${documentName} conversion is not supported.`

          );

        }



        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (!targetType) {

          return rejectWithValue(

            "Conversion target is required."

          );

        }



        if (

          typeof urls.convert !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} convert API is not configured.`

          );

        }



        const response =

          await api.post(

            urls.convert(

              documentId

            ),

            {

              targetType,

              saveAsDraft,

              payments,

              ...payload,

            }

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to convert ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return {

          data:

            getResponseData(

              response,

              {}

            ),



          message:

            response?.message ||

            `${documentName} converted successfully.`,

        };

      }

    );



  /*

  |--------------------------------------------------------------------------

  | DELETE

  |--------------------------------------------------------------------------

  */



  const deleteDocument =

    createAsyncThunk(

      `${actionPrefix}/deleteDocument`,

      async (

        documentId,

        {

          rejectWithValue,

        }

      ) => {

        if (!supportsDelete) {

          return rejectWithValue(

            `${documentName} deletion is not supported.`

          );

        }



        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (

          typeof urls.delete !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} delete API is not configured.`

          );

        }



        const response =

          await api.delete(

            urls.delete(

              documentId

            )

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to delete ${documentName.toLowerCase()}.`

          );



        if (failed) {

          return failed;

        }



        return {

          documentId,



          data:

            getResponseData(

              response,

              {}

            ),



          message:

            response?.message ||

            `${documentName} deleted successfully.`,

        };

      }

    );



  /*

  |--------------------------------------------------------------------------

  | HISTORY

  |--------------------------------------------------------------------------

  */



  const fetchDocumentHistory =

    createAsyncThunk(

      `${actionPrefix}/fetchDocumentHistory`,

      async (

        {

          documentId,

          params = {},

        },

        {

          rejectWithValue,

        }

      ) => {

        if (!supportsHistory) {

          return rejectWithValue(

            `${documentName} history is not supported.`

          );

        }



        if (!documentId) {

          return rejectWithValue(

            `${documentName} ID is required.`

          );

        }



        if (

          typeof urls.history !==

          "function"

        ) {

          return rejectWithValue(

            `${documentName} history API is not configured.`

          );

        }



        const response =

          await api.getWithParams(
            urls.history(

              documentId

            ),

            params

          );



        const failed =

          rejectFailedResponse(

            response,

            rejectWithValue,

            `Unable to fetch ${documentName.toLowerCase()} history.`

          );



        if (failed) {

          return failed;

        }



        return getResponseData(

          response,

          {}

        );

      }

    );



  /*

  |--------------------------------------------------------------------------

  | SLICE

  |--------------------------------------------------------------------------

  */



  const slice =

    createSlice({

      name:

        sliceName,



      initialState:

        createInitialState(),



      reducers: {

        setFilters: (

          state,

          action

        ) => {

          state.filters = {

            ...state.filters,

            ...(action.payload || {}),

          };

        },



        setFilter: (

          state,

          action

        ) => {

          const {

            key,

            value,

          } =

            action.payload || {};



          if (!key) {

            return;

          }



          state.filters[key] =

            value;



          if (

            key !== "page" &&

            key !== "limit"

          ) {

            state.filters.page = 1;

          }

        },



        setPage: (

          state,

          action

        ) => {

          state.filters.page =

            Number(

              action.payload

            ) || 1;

        },



        setLimit: (

          state,

          action

        ) => {

          state.filters.limit =

            Number(

              action.payload

            ) || 20;



          state.filters.page = 1;

        },



        resetFilters: (

          state

        ) => {

          state.filters = {

            ...resolvedInitialFilters,

          };

        },



        setSelectedDocument: (

          state,

          action

        ) => {

          state.selectedDocument =

            action.payload || null;

        },



        clearSelectedDocument: (

          state

        ) => {

          state.selectedDocument =

            null;

        },



        clearHistory: (

          state

        ) => {

          state.history = [];



          state.historyPagination = {

            ...defaultPagination,

          };



          state.historyLoading =

            false;

        },



        clearErrors: (

          state

        ) => {

          state.error = null;

          state.actionError = null;

        },



        clearOperation: (

          state

        ) => {

          state.operationSuccess =

            false;



          state.operationMessage =

            "";



          state.actionError =

            null;

        },



        clearLastCreatedDocument: (

          state

        ) => {

          state.lastCreatedDocument =

            null;

        },



        clearLastDuplicatedDocument: (

          state

        ) => {

          state.lastDuplicatedDocument =

            null;

        },



        clearLastConvertedDocument: (

          state

        ) => {

          state.lastConvertedDocument =

            null;

        },



        resetState:

          () =>

            createInitialState(),

      },



      extraReducers: (

        builder

      ) => {

        /*

        |--------------------------------------------------------------------------

        | LIST

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            fetchDocuments.pending,

            (state) => {

              state.listLoading =

                true;



              state.error = null;

            }

          )

          .addCase(

            fetchDocuments.fulfilled,

            (

              state,

              action

            ) => {

              state.listLoading =

                false;



              const payload =

                action.payload || {};



              state.documents =

                getListFromPayload(

                  payload,

                  resolvedListKeys

                );



              state.pagination = {

                ...defaultPagination,

                ...(payload.pagination ||

                  {}),

              };



              if (

                payload.pagination

                  ?.page

              ) {

                state.filters.page =

                  payload

                    .pagination

                    .page;

              }



              if (

                payload.pagination

                  ?.limit

              ) {

                state.filters.limit =

                  payload

                    .pagination

                    .limit;

              }

            }

          )

          .addCase(

            fetchDocuments.rejected,

            (

              state,

              action

            ) => {

              state.listLoading =

                false;



              state.error =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to fetch ${documentName.toLowerCase()} list.`;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | SUMMARY

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            fetchSummary.pending,

            (state) => {

              state.summaryLoading =

                true;



              state.error = null;

            }

          )

          .addCase(

            fetchSummary.fulfilled,

            (

              state,

              action

            ) => {

              state.summaryLoading =

                false;



              const payload =

                action.payload || {};



              state.summary = {

                ...resolvedInitialSummary,

                ...(payload.summary ||

                  payload),

              };

            }

          )

          .addCase(

            fetchSummary.rejected,

            (

              state,

              action

            ) => {

              state.summaryLoading =

                false;



              state.error =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to fetch ${documentName.toLowerCase()} summary.`;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | CREATE DATA

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            fetchCreateData.pending,

            (state) => {

              state.createDataLoading =

                true;



              state.error = null;

            }

          )

          .addCase(

            fetchCreateData.fulfilled,

            (

              state,

              action

            ) => {

              state.createDataLoading =

                false;



              state.createData =

                action.payload ||

                null;

            }

          )

          .addCase(

            fetchCreateData.rejected,

            (

              state,

              action

            ) => {

              state.createDataLoading =

                false;



              state.error =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to fetch ${documentName.toLowerCase()} create data.`;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | DROPDOWNS

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            fetchDropdowns.pending,

            (state) => {

              state.dropdownsLoading =

                true;



              state.error = null;

            }

          )

          .addCase(

            fetchDropdowns.fulfilled,

            (

              state,

              action

            ) => {

              state.dropdownsLoading =

                false;



              const payload =

                action.payload || {};



              state.dropdowns = {

                ...state.dropdowns,

                ...(payload.dropdowns ||

                  payload),

              };

            }

          )

          .addCase(

            fetchDropdowns.rejected,

            (

              state,

              action

            ) => {

              state.dropdownsLoading =

                false;



              state.error =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to fetch ${documentName.toLowerCase()} dropdowns.`;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | DETAILS

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            fetchDocumentById.pending,

            (state) => {

              state.detailsLoading =

                true;



              state.error = null;

            }

          )

          .addCase(

            fetchDocumentById.fulfilled,

            (

              state,

              action

            ) => {

              state.detailsLoading =

                false;



              state.selectedDocument =

                getDocumentFromPayload(

                  action.payload,

                  resolvedDocumentKeys

                );

            }

          )

          .addCase(

            fetchDocumentById.rejected,

            (

              state,

              action

            ) => {

              state.detailsLoading =

                false;



              state.error =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to fetch ${documentName.toLowerCase()}.`;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | CREATE

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            createDocument.pending,

            (state) => {

              state.createLoading =

                true;



              state.actionError =

                null;



              state.operationSuccess =

                false;



              state.operationMessage =

                "";

            }

          )

          .addCase(

            createDocument.fulfilled,

            (

              state,

              action

            ) => {

              state.createLoading =

                false;



              const document =

                getDocumentFromPayload(

                  action.payload

                    ?.data,

                  resolvedDocumentKeys

                );



              state.lastCreatedDocument =

                document;



              state.selectedDocument =

                document;



              addDocumentToList(

                state,

                document

              );



              state.operationSuccess =

                true;



              state.operationMessage =

                action.payload

                  ?.message ||

                `${documentName} created successfully.`;

            }

          )

          .addCase(

            createDocument.rejected,

            (

              state,

              action

            ) => {

              state.createLoading =

                false;



              state.actionError =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to create ${documentName.toLowerCase()}.`;



              state.operationSuccess =

                false;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | UPDATE

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            updateDocument.pending,

            (state) => {

              state.updateLoading =

                true;



              state.actionError =

                null;



              state.operationSuccess =

                false;

            }

          )

          .addCase(

            updateDocument.fulfilled,

            (

              state,

              action

            ) => {

              state.updateLoading =

                false;



              const document =

                getDocumentFromPayload(

                  action.payload

                    ?.data,

                  resolvedDocumentKeys

                );



              state.selectedDocument =

                document;



              updateDocumentInList(

                state,

                document

              );



              state.operationSuccess =

                true;



              state.operationMessage =

                action.payload

                  ?.message ||

                `${documentName} updated successfully.`;

            }

          )

          .addCase(

            updateDocument.rejected,

            (

              state,

              action

            ) => {

              state.updateLoading =

                false;



              state.actionError =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to update ${documentName.toLowerCase()}.`;



              state.operationSuccess =

                false;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | CONFIRM

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            confirmDocument.pending,

            (state) => {

              state.confirmLoading =

                true;



              state.actionError =

                null;



              state.operationSuccess =

                false;

            }

          )

          .addCase(

            confirmDocument.fulfilled,

            (

              state,

              action

            ) => {

              state.confirmLoading =

                false;



              const document =

                getDocumentFromPayload(

                  action.payload

                    ?.data,

                  resolvedDocumentKeys

                );



              state.selectedDocument =

                document;



              updateDocumentInList(

                state,

                document

              );



              state.operationSuccess =

                true;



              state.operationMessage =

                action.payload

                  ?.message ||

                `${documentName} confirmed successfully.`;

            }

          )

          .addCase(

            confirmDocument.rejected,

            (

              state,

              action

            ) => {

              state.confirmLoading =

                false;



              state.actionError =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to confirm ${documentName.toLowerCase()}.`;



              state.operationSuccess =

                false;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | CANCEL

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            cancelDocument.pending,

            (state) => {

              state.cancelLoading =

                true;



              state.actionError =

                null;



              state.operationSuccess =

                false;

            }

          )

          .addCase(

            cancelDocument.fulfilled,

            (

              state,

              action

            ) => {

              state.cancelLoading =

                false;



              const document =

                getDocumentFromPayload(

                  action.payload

                    ?.data,

                  resolvedDocumentKeys

                );



              state.selectedDocument =

                document;



              updateDocumentInList(

                state,

                document

              );



              state.operationSuccess =

                true;



              state.operationMessage =

                action.payload

                  ?.message ||

                `${documentName} cancelled successfully.`;

            }

          )

          .addCase(

            cancelDocument.rejected,

            (

              state,

              action

            ) => {

              state.cancelLoading =

                false;



              state.actionError =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to cancel ${documentName.toLowerCase()}.`;



              state.operationSuccess =

                false;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | DUPLICATE

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            duplicateDocument.pending,

            (state) => {

              state.duplicateLoading =

                true;



              state.actionError =

                null;



              state.operationSuccess =

                false;

            }

          )

          .addCase(

            duplicateDocument.fulfilled,

            (

              state,

              action

            ) => {

              state.duplicateLoading =

                false;



              const document =

                getDocumentFromPayload(

                  action.payload

                    ?.data,

                  resolvedDocumentKeys

                );



              state.lastDuplicatedDocument =

                document;



              addDocumentToList(

                state,

                document

              );



              state.operationSuccess =

                true;



              state.operationMessage =

                action.payload

                  ?.message ||

                `${documentName} duplicated successfully.`;

            }

          )

          .addCase(

            duplicateDocument.rejected,

            (

              state,

              action

            ) => {

              state.duplicateLoading =

                false;



              state.actionError =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to duplicate ${documentName.toLowerCase()}.`;



              state.operationSuccess =

                false;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | CONVERT

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            convertDocument.pending,

            (state) => {

              state.convertLoading =

                true;



              state.actionError =

                null;



              state.operationSuccess =

                false;

            }

          )

          .addCase(

            convertDocument.fulfilled,

            (

              state,

              action

            ) => {

              state.convertLoading =

                false;



              const payload =

                action.payload

                  ?.data ||

                {};



              const sourceDocument =

                getDocumentFromPayload(

                  payload,

                  [

                    `source${capitalizeFirst(

                      documentKey ||

                      sliceName

                    )}`,



                    "sourceDocument",

                    ...resolvedDocumentKeys,

                  ]

                );



              const convertedDocument =

                payload

                  .convertedDocument ||

                payload

                  .targetDocument ||

                null;



              state.lastConvertedDocument =

                convertedDocument;



              if (

                sourceDocument?._id

              ) {

                state.selectedDocument =

                  sourceDocument;



                updateDocumentInList(

                  state,

                  sourceDocument

                );

              }



              state.operationSuccess =

                true;



              state.operationMessage =

                action.payload

                  ?.message ||

                `${documentName} converted successfully.`;

            }

          )

          .addCase(

            convertDocument.rejected,

            (

              state,

              action

            ) => {

              state.convertLoading =

                false;



              state.actionError =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to convert ${documentName.toLowerCase()}.`;



              state.operationSuccess =

                false;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | DELETE

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            deleteDocument.pending,

            (state) => {

              state.deleteLoading =

                true;



              state.actionError =

                null;



              state.operationSuccess =

                false;

            }

          )

          .addCase(

            deleteDocument.fulfilled,

            (

              state,

              action

            ) => {

              state.deleteLoading =

                false;



              const documentId =

                action.payload

                  ?.documentId;



              state.documents =

                state.documents.filter(

                  (document) =>

                    document._id !==

                    documentId

                );



              if (

                state

                  .selectedDocument

                  ?._id ===

                documentId

              ) {

                state.selectedDocument =

                  null;

              }



              state.operationSuccess =

                true;



              state.operationMessage =

                action.payload

                  ?.message ||

                `${documentName} deleted successfully.`;

            }

          )

          .addCase(

            deleteDocument.rejected,

            (

              state,

              action

            ) => {

              state.deleteLoading =

                false;



              state.actionError =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to delete ${documentName.toLowerCase()}.`;



              state.operationSuccess =

                false;

            }

          );



        /*

        |--------------------------------------------------------------------------

        | HISTORY

        |--------------------------------------------------------------------------

        */



        builder

          .addCase(

            fetchDocumentHistory.pending,

            (state) => {

              state.historyLoading =

                true;



              state.error = null;

            }

          )

          .addCase(

            fetchDocumentHistory.fulfilled,

            (

              state,

              action

            ) => {

              state.historyLoading =

                false;



              const payload =

                action.payload || {};



              state.history =

                payload.history ||

                payload.records ||

                payload.items ||

                [];



              state.historyPagination = {

                ...defaultPagination,

                ...(payload.pagination ||

                  {}),

              };

            }

          )

          .addCase(

            fetchDocumentHistory.rejected,

            (

              state,

              action

            ) => {

              state.historyLoading =

                false;



              state.error =

                action.payload ||

                action.error

                  ?.message ||

                `Unable to fetch ${documentName.toLowerCase()} history.`;

            }

          );

      },

    });



  /*

  |--------------------------------------------------------------------------

  | SELECTORS

  |--------------------------------------------------------------------------

  */



  const selectModule = (

    state

  ) =>

    state?.[stateKey] ||

    createInitialState();



  const selectors = {

    selectModule,



    selectDocuments: (

      state

    ) =>

      selectModule(state)

        .documents,



    selectSelectedDocument: (

      state

    ) =>

      selectModule(state)

        .selectedDocument,



    selectSummary: (

      state

    ) =>

      selectModule(state)

        .summary,



    selectFilters: (

      state

    ) =>

      selectModule(state)

        .filters,



    selectPagination: (

      state

    ) =>

      selectModule(state)

        .pagination,



    selectCreateData: (

      state

    ) =>

      selectModule(state)

        .createData,



    selectDropdowns: (

      state

    ) =>

      selectModule(state)

        .dropdowns,



    selectHistory: (

      state

    ) => {

      const moduleState =

        selectModule(state);



      return {

        history:

          moduleState.history,



        pagination:

          moduleState

            .historyPagination,



        loading:

          moduleState

            .historyLoading,

      };

    },



    selectLoading: (

      state

    ) => {

      const moduleState =

        selectModule(state);



      return {

        listLoading:

          moduleState

            .listLoading,



        detailsLoading:

          moduleState

            .detailsLoading,



        summaryLoading:

          moduleState

            .summaryLoading,



        createDataLoading:

          moduleState

            .createDataLoading,



        dropdownsLoading:

          moduleState

            .dropdownsLoading,



        historyLoading:

          moduleState

            .historyLoading,



        createLoading:

          moduleState

            .createLoading,



        updateLoading:

          moduleState

            .updateLoading,



        confirmLoading:

          moduleState

            .confirmLoading,



        cancelLoading:

          moduleState

            .cancelLoading,



        duplicateLoading:

          moduleState

            .duplicateLoading,



        convertLoading:

          moduleState

            .convertLoading,



        deleteLoading:

          moduleState

            .deleteLoading,

      };

    },



    selectOperation: (

      state

    ) => {

      const moduleState =

        selectModule(state);



      return {

        success:

          moduleState

            .operationSuccess,



        message:

          moduleState

            .operationMessage,



        error:

          moduleState

            .actionError,



        lastCreatedDocument:

          moduleState

            .lastCreatedDocument,



        lastDuplicatedDocument:

          moduleState

            .lastDuplicatedDocument,



        lastConvertedDocument:

          moduleState

            .lastConvertedDocument,

      };

    },

  };



  return {

    reducer:

      slice.reducer,



    actions:

      slice.actions,



    thunks: {

      fetchDocuments,

      fetchSummary,

      fetchCreateData,

      fetchDropdowns,

      fetchDocumentById,



      createDocument,

      updateDocument,

      confirmDocument,

      cancelDocument,

      duplicateDocument,

      convertDocument,

      deleteDocument,



      fetchDocumentHistory,

    },



    selectors,



    initialState:

      createInitialState(),



    documentType,

    documentName,

    sliceName,

    stateKey,

  };

};



export default createDocumentSlice;